import { createApi } from '@reduxjs/toolkit/query/react'
import { ARCHIVE_NOTIFICATION, EDIT_NOTIFICATION_TEMPLATE, MARK_ALL_NOTIFICATIONS_READ, MARK_SINGLE_NOTIFICATION_READ, NOTIFICATIONS_FILTER_TYPES, NOTIFICATIONS_LIST, UNARCHIVE_NOTIFICATION, UNREAD_NOTIFCATIONS_COUNT, NOTIFICATION_MANAGE_CATEGORIES, NOTIFICATION_MANAGE_USERS, NOTIFICATION_MANAGE_USER_FEED, NOTIFICATION_TEMPLATES_LIST, NOTIFICATION_TEMPLATE_VIEW } from 'config/apiPaths';
import { NOTIFICATION_TEMPLATES } from 'config/paths';
import axiosBaseQuery from "utils/axios/axiosBaseQuery"

export const notificationsApi = createApi({
    reducerPath: 'notificationsApi',
    baseQuery: axiosBaseQuery({
        baseUrl: "/notification",
    }),
    tagTypes: ["NotificationsList", "UnreadNotificationsCount"],
    endpoints: (builder) => ({

        // classroom consultation list
        getNotifications: builder.query({
            query: ({ category = "", type = "", isRead = "", isCompleted = "", isArchived = false, page = 1, limit = 10, sort = { column: "id", direction: "ASC" } }) => {
                return ({
                    method: "PATCH",
                    url: NOTIFICATIONS_LIST,
                    data: { category, type: type?.value || "", isRead, isCompleted, isArchived: isArchived?.value ?? isArchived, page, limit, sort }
                })
            },
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["NotificationsList"]
        }),

        // add classroom consultation
        markSingleNotificationRead: builder.mutation({
            query: ({ id }) => ({
                method: "PATCH",
                url: `/${id}${MARK_SINGLE_NOTIFICATION_READ}`,
            }),
            invalidatesTags: ["NotificationsList", "UnreadNotificationsCount"]
        }),

        markAllNotificationRead: builder.mutation({
            query: () => ({
                method: "PATCH",
                url: MARK_ALL_NOTIFICATIONS_READ,
            }),
            invalidatesTags: ["NotificationsList", "UnreadNotificationsCount"]
        }),

        archiveNotification: builder.mutation({
            query: ({ id }) => ({
                method: "PATCH",
                url: `/${id}${ARCHIVE_NOTIFICATION}`,
            }),
            invalidatesTags: ["NotificationsList", "UnreadNotificationsCount"]
        }),

        unarchiveNotification: builder.mutation({
            query: ({ id }) => ({
                method: "PATCH",
                url: `/${id}${UNARCHIVE_NOTIFICATION}`,
            }),
            invalidatesTags: ["NotificationsList", "UnreadNotificationsCount"]
        }),

        getNotificationsFilterTypes: builder.query({
            query: () => {
                return ({
                    method: "GET",
                    url: NOTIFICATIONS_FILTER_TYPES,
                })
            },
            transformResponse: (response) => {
                return response.data;
            },
        }),

        getUnreadNotificationsCount: builder.query({
            query: () => {
                return ({
                    method: "GET",
                    url: UNREAD_NOTIFCATIONS_COUNT,
                })
            },
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["UnreadNotificationsCount"],
        }),

        getNotificationCategories: builder.query({
            query: () => {
                return ({
                    method: "GET",
                    url: NOTIFICATION_MANAGE_CATEGORIES,
                })
            },
            transformResponse: (response) => {
                return response.data;
            },
        }),

        getNotificationManagementUsers: builder.query({
            query: ({ category, search = "", page = 1, limit = 20 }) => ({
                method: "PATCH",
                url: `${NOTIFICATION_MANAGE_USERS}/${category}`,
                data: { search, page, limit },
            }),
            transformResponse: (response) => response.data,
        }),

        getNotificationManagementUserFeed: builder.query({
            query: ({ userId, category = "", type = "", sort = { column: "id", direction: "ASC" }, page = 1, limit = 10 }) => ({
                method: "PATCH",
                url: `${NOTIFICATION_MANAGE_USER_FEED}/${userId}`,
                data: { category, type: type?.value || "", sort, page, limit },
            }),
            transformResponse: (response) => response.data,
        }),

        getNotificationTemplates: builder.query({
            query: () => ({
                method: "GET",
                url: NOTIFICATION_TEMPLATES_LIST,
            }),
            providesTags: ["NotificationsTemplatesList"],
            transformResponse: (response) => response.data,
        }),

        getNotificationTemplateView: builder.query({
            query: ({ id }) => ({
                method: "GET",
                url: `${NOTIFICATION_TEMPLATE_VIEW}/${id}`,
            }),
            providesTags: ["NotificationsTemplateView"],
            transformResponse: (response) => response.data,
        }),

        editNotificationTemplate: builder.mutation({
            query: ({ id, data }) => ({
                method: "PUT",
                url: `/${EDIT_NOTIFICATION_TEMPLATE}/${id}`,
                data
            }),
            invalidatesTags: ["NotificationsTemplatesList", "NotificationsTemplateView"]
        }),

    }),
})

export const {
    useGetNotificationsQuery,
    useMarkSingleNotificationReadMutation,
    useMarkAllNotificationReadMutation,
    useArchiveNotificationMutation,
    useUnarchiveNotificationMutation,
    useGetNotificationsFilterTypesQuery,
    useGetUnreadNotificationsCountQuery,
    useGetNotificationCategoriesQuery,
    useGetNotificationManagementUsersQuery,
    useGetNotificationManagementUserFeedQuery,
    useGetNotificationTemplatesQuery,
    useGetNotificationTemplateViewQuery,
    useEditNotificationTemplateMutation
} = notificationsApi;
